import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import type { RowFilterSpec, TableSpec } from "@/lib/utils/daylight-insight/dashboardSpec";
import { RangeSlider } from "@/components/daylight-insight/controls";
import { useBranchDetails } from "@/components/daylight-insight/api/useBranchDetails";
import { useClinicianDetails } from "@/components/daylight-insight/api/useClinicianDetails";
import { unwrapRows } from "@/components/daylight-insight/api/unwrapRows";
import type {
  BranchDetailsParams,
  ClinicianDetailRow,
  ClinicianDetailsParams,
  DashboardMetricCategory,
} from "@/components/daylight-insight/api/types";
import BranchTablePanelSkeleton from "./BranchTablePanelSkeleton";
import ClinicianDetailsTableSkeleton from "./ClinicianDetailsTableSkeleton";

// Lazy so these chunks never block the dashboard's initial render.
const ClinicianDetailsTable = lazy(() => import("./ClinicianDetailsTable"));
const BranchTablePanel = lazy(() => import("./BranchTablePanel"));

/**
 * Everything a detail table needs from the page it sits on: the scope every
 * call is keyed on, the server-side filter selections, and where the page's
 * load chain stands.
 */
export interface DashboardTableContext {
  /** Remount key for the By-Branch panel — a dashboard or service switch starts
   *  its column layout fresh. */
  remountKey: string;
  dashboardID: number | string | null;
  serviceLineSystemID: string;
  range: { start: string; end: string };
  /** The applied selection keyed by config filter `key` — the SPs' JSON
   *  `@Filters`. Null when nothing is selected. */
  filters: Record<string, string[]> | null;
  /** The dashboard's metric metadata — the By-Branch table's columns. */
  category: DashboardMetricCategory | null;
  /** The dashboard-metrics call hasn't resolved (or can't fire) yet. */
  metricsPending: boolean;
  /** The dashboard-metrics call resolved with metrics: secondary calls may go. */
  metricsReady: boolean;
  /** Clinician grain only: whatever the table must wait for beyond the metrics
   *  (the by-Discipline card's snapshot call, when the page carries one). */
  clinicianReady: boolean;
  /** Clinician grain only: an upstream call failed, so the table's own call
   *  will never fire — hide the section rather than shimmer forever. */
  clinicianUpstreamError: boolean;
}

/**
 * One entry of the dashboard spec's `tables[]`.
 *
 * Each table is its own component so it owns its own fetch hook: the page
 * renders however many tables the config lists, and a hook can't be called a
 * variable number of times from one component. Two tables of the same kind
 * share their request through the query cache (same params, same key).
 */
export function DashboardTable({ table, ctx }: { table: TableSpec; ctx: DashboardTableContext }) {
  switch (table.kind) {
    case "branch":
      return <BranchTableSection table={table} ctx={ctx} />;
    case "clinician":
      return <ClinicianTableSection table={table} ctx={ctx} />;
    default:
      return null;
  }
}

/**
 * By-Branch table — its own branch-details API, chained behind the metrics
 * call so the KPIs paint first: while the metrics are pending only the
 * skeleton renders (no fetch, no chunk), then the lazy panel mounts and runs
 * its own request. Columns use `category` metadata; the per-branch values come
 * from branch-details. Every page filter reaches the SP as `filters`.
 */
function BranchTableSection({ table, ctx }: { table: TableSpec; ctx: DashboardTableContext }) {
  const params: BranchDetailsParams | null =
    ctx.metricsReady && ctx.dashboardID != null && ctx.serviceLineSystemID
      ? {
          dashboardID: ctx.dashboardID,
          serviceLineSystemID: ctx.serviceLineSystemID,
          startDate: ctx.range.start,
          endDate: ctx.range.end,
          filters: ctx.filters,
        }
      : null;
  const details = useBranchDetails(params);

  if (ctx.metricsPending) return <BranchTablePanelSkeleton />;
  if (!ctx.metricsReady) return null;
  return (
    <Suspense fallback={<BranchTablePanelSkeleton />}>
      <BranchTablePanel
        key={ctx.remountKey}
        category={ctx.category}
        data={details.data}
        loading={details.loading || params == null}
        error={details.error}
        dashboardID={ctx.dashboardID}
        title={table.title}
      />
    </Suspense>
  );
}

// ── By-Clinician row sliders ────────────────────────────────────────────────
// Which columns they filter on comes from the table's spec (`rowFilters`).
//
// The configured name is matched against the SP's own column name loosely
// enough to survive punctuation and a pluralised word ("% Eligible ETA Text
// Sent" vs "Eligible ETA Texts Sent") but still WHOLE-name, so a sibling column
// can't be picked up by accident — "Texts Sent" must not answer for
// "% Eligible ETA Text Sent".
const colTokens = (name: string): string =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .split(" ")
    .map((w) => w.replace(/s$/, ""))
    .join(" ");

const findCol = (keys: string[], column: string): string | undefined => {
  const want = colTokens(column);
  return keys.find((k) => colTokens(k) === want);
};

const NO_ROW_FILTERS: RowFilterSpec[] = [];

/**
 * By-Clinician table — driven by `POST /dashboard/clinician-details`; the SP
 * decides the columns and the table renders whatever comes back.
 *
 * Chaining: the page decides `clinicianReady` — a dashboard whose
 * by-Discipline card sits above the table waits for that card's snapshot call
 * too; one with no card in between goes as soon as the metrics land.
 */
function ClinicianTableSection({ table, ctx }: { table: TableSpec; ctx: DashboardTableContext }) {
  const rowFilterSpecs = table.rowFilters ?? NO_ROW_FILTERS;

  const params: ClinicianDetailsParams | null =
    ctx.clinicianReady && ctx.serviceLineSystemID
      ? {
          serviceLineSystemID: ctx.serviceLineSystemID,
          startDate: ctx.range.start,
          endDate: ctx.range.end,
          filters: ctx.filters,
        }
      : null;
  const details = useClinicianDetails(params);

  // The table's client-side filters. Unlike the page's filter bar they are NOT
  // server-side — the clinician-details SP takes no such parameters — so they
  // narrow this table's own rows and live in its toolbar.
  //
  // `null` means "untouched", i.e. the whole domain: a freshly loaded table is
  // never pre-filtered, and the sliders only start excluding rows once the user
  // actually drags one. Keyed by the spec's column name, so a third slider needs
  // no state of its own here.
  const [rowRanges, setRowRanges] = useState<Record<string, [number, number] | null>>({});

  // A slider with no `domain` in the spec reads its bounds off the ROWS — a
  // count has no sensible fixed maximum and a guessed one would hide every
  // clinician above it. A column the response doesn't carry resolves to null and
  // its slider isn't rendered at all, so a filter never empties a table over a
  // column that isn't there.
  const clinicianRows = details.data;
  const rowDomains = useMemo(() => {
    const rows = unwrapRows<ClinicianDetailRow>(clinicianRows);
    const out = new Map<string, [number, number] | null>();
    for (const f of rowFilterSpecs) {
      if (f.domain) {
        out.set(f.column, f.domain);
        continue;
      }
      let lo = Infinity;
      let hi = -Infinity;
      for (const r of rows) {
        const key = findCol(Object.keys(r), f.column);
        if (key === undefined) continue;
        const raw = r[key];
        // A blank cell means "no value", not zero: Number(null) and Number("")
        // both come back as 0 and would drag the floor down to it. Same parse
        // the table itself does, so the bounds match the column on screen.
        if (raw === null || raw === undefined || raw === "") continue;
        const n = Number(raw);
        if (!Number.isFinite(n)) continue;
        if (n < lo) lo = n;
        if (n > hi) hi = n;
      }
      if (lo > hi) {
        out.set(f.column, null);
        continue;
      }
      lo = Math.floor(lo);
      hi = Math.ceil(hi);
      // RangeSlider needs room between the handles (it clamps to ±step).
      out.set(f.column, hi > lo ? [lo, hi] : [lo, lo + 1]);
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicianRows, JSON.stringify(rowFilterSpecs)]);

  // A page filter re-fires the query and the domains move with it, so a range
  // picked against the old data can sit entirely outside the new one — which
  // would empty the table with the handles parked off the track. Drop every
  // slider back to its full range whenever the domains shift.
  const domainKey = JSON.stringify([...rowDomains.entries()]);
  useEffect(() => {
    setRowRanges({});
  }, [domainKey]);

  // The All / Full Time / Part Time segmented toggle that used to lead this
  // toolbar is hidden for now, so the sliders are all that's left.
  const sliders = rowFilterSpecs
    .map((f) => ({ f, domain: rowDomains.get(f.column) ?? null }))
    .filter((x): x is { f: RowFilterSpec; domain: [number, number] } => x.domain !== null);
  const toolbar = sliders.length ? (
    <>
      {sliders.map(({ f, domain }) => (
        <RangeSlider
          key={f.column}
          label={f.label ?? f.column}
          min={domain[0]}
          max={domain[1]}
          step={1}
          value={rowRanges[f.column] ?? domain}
          onChange={(v) => setRowRanges((prev) => ({ ...prev, [f.column]: v }))}
          fmt={(v) => (f.unit === "percent" ? v + "%" : v.toLocaleString())}
        />
      ))}
    </>
  ) : undefined;

  // …and the predicate they drive. The columns are the SP's, so each slider
  // looks its column up BY NAME and stands down when the response doesn't carry
  // one. An untouched slider (`null`) is skipped outright, so rows with a blank
  // value in that column survive until the user actually narrows the range.
  const rowFilter = useMemo(() => {
    const active = rowFilterSpecs
      .map((f) => ({ column: f.column, range: rowRanges[f.column] ?? null }))
      .filter((x): x is { column: string; range: [number, number] } => x.range !== null);
    if (!active.length) return undefined;
    return (cells: Record<string, string | number | null>) => {
      const keys = Object.keys(cells);
      for (const { column, range } of active) {
        const k = findCol(keys, column);
        if (k === undefined) continue;
        const v = cells[k];
        if (typeof v === "number" && (v < range[0] || v > range[1])) return false;
      }
      return true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowRanges, JSON.stringify(rowFilterSpecs)]);

  // Same three states as the other sections; `loading || params == null` keeps
  // the skeleton up while the chain is still resolving.
  if (ctx.clinicianUpstreamError) return null;
  if (ctx.metricsPending) return <ClinicianDetailsTableSkeleton />;
  if (!ctx.metricsReady) return null;
  return (
    <Suspense fallback={<ClinicianDetailsTableSkeleton />}>
      <ClinicianDetailsTable
        data={details.data}
        loading={details.loading || params == null}
        error={details.error}
        dashboardID={ctx.dashboardID}
        toolbarExtras={toolbar}
        rowFilter={rowFilter}
        title={table.title}
      />
    </Suspense>
  );
}
